/**
 * Calendar event color theming system.
 *
 * Each calendar gets a consistent color theme based on its ID.
 * Themes define background, border, title, and time colors
 * via CSS classes in calendar.css.
 */

import { getGoogleEventColorHex } from "@/components/calendars/googleEventColors.ts";
import { getOutlookPresetHex } from "@/components/calendars/outlookCategoryColors.ts";
import { useMemo, useSyncExternalStore } from "react";

export type EventThemeKey =
  | "primary"
  | "gray"
  | "blue"
  | "green"
  | "orange"
  | "purple"
  | "rose"
  | "teal"
  | "amber";

const CALENDAR_THEME_KEYS: EventThemeKey[] = [
  "blue",
  "green",
  "orange",
  "purple",
  "rose",
  "teal",
  "amber",
];

const isPrimaryCalendar = (calendarId: string) =>
  calendarId === "primary" ||
  (calendarId.includes("@") &&
    !calendarId.includes("group.calendar.google.com") &&
    !calendarId.includes("#"));

/**
 * Get a color theme key for a calendar event.
 * Primary calendar events use the "primary" theme.
 * Other calendars cycle through the color palette.
 */
export const getCalendarEventTheme = (
  calendarId: string,
  allCalendarIds: string[],
): EventThemeKey => {
  if (isPrimaryCalendar(calendarId)) {
    return "primary";
  }

  const uniqueIds = [...new Set(allCalendarIds)].filter((id) => !isPrimaryCalendar(id)).sort();

  const index = uniqueIds.indexOf(calendarId);
  return CALENDAR_THEME_KEYS[Math.max(0, index) % CALENDAR_THEME_KEYS.length];
};

/**
 * Fallback hex colors used when a calendar has no color from Google.
 * These are the standard Google Calendar color palette.
 */
/**
 * The palette offered when a user picks a calendar's colour themselves. Only
 * okthx calendars are user-colourable — provider calendar colours are read-only
 * and come from the provider — so today this backs the Reminders calendar.
 */
export const CALENDAR_COLOR_CHOICES = [
  "#8E44AD", // Grape — the Reminders default
  "#039BE5", // Peacock
  "#33B679", // Sage
  "#E67C73", // Flamingo
  "#F6BF26", // Banana
  "#F4511E", // Tangerine
  "#3F51B5", // Blueberry
  "#616161", // Graphite
] as const;

const FALLBACK_COLORS = [
  "#039BE5", // Peacock
  "#7986CB", // Lavender
  "#33B679", // Sage
  "#8E24AA", // Grape
  "#E67C73", // Flamingo
  "#F6BF26", // Banana
  "#F4511E", // Tangerine
  "#616161", // Graphite
  "#3F51B5", // Blueberry
  "#0B8043", // Basil
  "#D50000", // Tomato
  "#AD1457", // Radicchio
];

/**
 * Google Calendar API returns legacy pastel hex values in backgroundColor,
 * but the modern UI displays bolder Material Design colors.
 * This maps legacy → modern so our calendar matches what users see in Google Calendar.
 */
const LEGACY_TO_MODERN: Record<string, string> = {
  "#ac725e": "#795548", // Cocoa
  "#d06b64": "#E67C73", // Flamingo
  "#f83a22": "#D50000", // Tomato
  "#fa573c": "#F4511E", // Tangerine
  "#ff7537": "#EF6C00", // Pumpkin
  "#ffad46": "#F09300", // Mango
  "#42d692": "#009688", // Eucalyptus
  "#16a765": "#0B8043", // Basil
  "#7bd148": "#7CB342", // Pistachio
  "#b3dc6c": "#C0CA33", // Avocado
  "#fbe983": "#E4C441", // Citron
  "#fad165": "#F6BF26", // Banana
  "#92e1c0": "#33B679", // Sage
  "#9fe1e7": "#039BE5", // Peacock
  "#9fc6e7": "#4285F4", // Blueberry
  "#4986e7": "#3F51B5", // Lavender
  "#9a9cff": "#7986CB", // Wisteria
  "#b99aff": "#B39DDB", // Amethyst
  "#c2c2c2": "#616161", // Graphite
  "#cabdbf": "#A79B8E", // Birch
  "#cca6ac": "#AD1457", // Radicchio
  "#f691b2": "#D81B60", // Cherry Blossom
  "#cd74e6": "#8E24AA", // Grape
  "#a47ae2": "#7986CB", // Wisteria
};

/** Convert a legacy Google Calendar color to its modern equivalent. */
const toModernColor = (hex: string): string => LEGACY_TO_MODERN[hex.toLowerCase()] ?? hex;

/**
 * Ensure a color is bold enough for display.
 * If lightness is too high (pastel/light custom colors), cap it so the color stays vibrant.
 * Standard mapped colors are already bold, so this mainly affects custom picks.
 */
function ensureBold(hex: string): string {
  const r = Number.parseInt(hex.slice(1, 3), 16) / 255;
  const g = Number.parseInt(hex.slice(3, 5), 16) / 255;
  const b = Number.parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  // If the color is already reasonably bold, leave it alone
  if (l <= 0.55) return hex;

  // Recompute in HSL and cap lightness
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }

  const nl = 0.5; // Target lightness — bold but not overly vibrant
  const hue2rgb = (p: number, q: number, t: number) => {
    const tn = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    if (tn < 1 / 6) return p + (q - p) * 6 * tn;
    if (tn < 1 / 2) return q;
    if (tn < 2 / 3) return p + (q - p) * (2 / 3 - tn) * 6;
    return p;
  };
  const q = nl < 0.5 ? nl * (1 + s) : nl + s - nl * s;
  const p = 2 * nl - q;
  const toHex = (c: number) =>
    Math.round(c * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(hue2rgb(p, q, h + 1 / 3))}${toHex(hue2rgb(p, q, h))}${toHex(hue2rgb(p, q, h - 1 / 3))}`;
}

/**
 * Map of calendar IDs to their hex colors — a provider's for a provider
 * calendar, the user's pick for an okthx one.
 * Populated by CalendarView / CalendarList from the calendars API.
 */
const calendarColorMap = new Map<string, string>();

// The registry is a module-level map, invisible to React: the sidebar swatch,
// the row's dot and the grid's chips all read it during render, so a color
// landing in the map schedules no render of its own and they keep the hex they
// last read. The grid's chips are the acute case — FullCalendar keeps them
// mounted, so no ancestor re-renders them either. This small external store is
// what those readers subscribe to.
const registryListeners = new Set<() => void>();
const subscribeToRegistry = (listener: () => void): (() => void) => {
  registryListeners.add(listener);
  return () => {
    registryListeners.delete(listener);
  };
};

type CalendarColorResolvers = {
  getCalendarHex: (calendarId: string) => string;
  getEventHex: (calendarId: string, colorId: string | undefined) => string;
};
const makeResolvers = (): CalendarColorResolvers => ({
  getCalendarHex: getCalendarHexColor,
  getEventHex: getEventHexColor,
});
let currentResolvers: CalendarColorResolvers | undefined;
const getCurrentResolvers = (): CalendarColorResolvers => {
  currentResolvers ??= makeResolvers();
  return currentResolvers;
};

/**
 * Register calendars' colors for later lookup. The list is authoritative — the
 * registry is REPLACED to mirror it, not merged into, so a color the user
 * cleared or a calendar that went away stops coloring anything.
 */
export const registerCalendarColors = (
  calendars: { calendarId: string; color?: string | null }[],
) => {
  const next = new Map<string, string>();
  for (const cal of calendars) {
    if (cal.color) {
      next.set(cal.calendarId, ensureBold(toModernColor(cal.color)));
    }
  }
  const changed =
    next.size !== calendarColorMap.size ||
    [...next].some(([calendarId, hex]) => calendarColorMap.get(calendarId) !== hex);
  if (!changed) return;
  calendarColorMap.clear();
  for (const [calendarId, hex] of next) {
    calendarColorMap.set(calendarId, hex);
  }
  // New identity synchronously — components rendering after this point in the
  // same pass pick it up — but notify subscribers in a microtask: registration
  // runs during render (see useRegisteredCalendarColors), and synchronously
  // scheduling state updates in OTHER components mid-render is a React error.
  currentResolvers = makeResolvers();
  queueMicrotask(() => {
    for (const listener of registryListeners) listener();
  });
};

/**
 * Subscribe to the color registry. The returned resolvers change identity
 * whenever a registration changes a color, so a component that reads a color
 * through them repaints when the user picks a new one. Prefer these over the
 * bare `getCalendarHexColor` / `getEventHexColor` anywhere the result is
 * rendered.
 */
export function useCalendarColors(): CalendarColorResolvers {
  return useSyncExternalStore(subscribeToRegistry, getCurrentResolvers);
}

/**
 * Keep the color registry in sync during render. Registration is deliberately
 * synchronous (a useMemo, not an effect): the render that first sees a refetched
 * calendar list is the one that must already read its new colors.
 */
export function useRegisteredCalendarColors(
  calendars: { calendarId: string; color?: string | null }[] | undefined,
) {
  useMemo(() => {
    if (calendars) registerCalendarColors(calendars);
  }, [calendars]);
}

/**
 * Get the hex color for a calendar.
 * Uses the real Google Calendar color if available, otherwise falls back to a deterministic assignment.
 */
export const getCalendarHexColor = (calendarId: string): string => {
  const registered = calendarColorMap.get(calendarId);
  if (registered) return registered;

  // Deterministic fallback based on hash of calendarId
  let hash = 0;
  for (let i = 0; i < calendarId.length; i++) {
    hash = (hash * 31 + calendarId.charCodeAt(i)) | 0;
  }
  return FALLBACK_COLORS[Math.abs(hash) % FALLBACK_COLORS.length];
};

/**
 * Resolve an event's stored `colorId` to a hex value, or `undefined` if
 * unknown. Handles both schemes — a Google id (`"1"`–`"11"`) and an Outlook
 * preset (`"preset0"`–`"preset24"`) — by trying each provider's palette. The
 * render path doesn't care which provider the event came from.
 */
export const getEventColorHex = (colorId: string | undefined): string | undefined =>
  getGoogleEventColorHex(colorId) ?? getOutlookPresetHex(colorId);

/**
 * Get the hex color to render for an event. Prefers the event's own
 * `colorId` override; otherwise falls back to the calendar color.
 */
export const getEventHexColor = (calendarId: string, colorId: string | undefined): string =>
  getEventColorHex(colorId) ?? getCalendarHexColor(calendarId);
